/** Lookup-backed argument editors (countries, languages, OTAs, tags, tours,
 *  events, delivery methods, nested rulesets). Each stores the exact
 *  dashboard wire shape — see the per-editor comments. */
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { listRulesets, type RulesetRef } from '@/api/notificationLookups';
import { listEvents } from '@/api/notifications';
import * as otasApi from '@/api/otas';
import { listCountries } from '@/api/pickers';
import * as tagCategoriesApi from '@/api/tagCategories';
import { notificationRulesetTagLookups } from '@/api/tagLookups';
import * as tagsApi from '@/api/tags';
import * as toursApi from '@/api/tours';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';
import { TagConditionInput, type TagConditionFetchers } from '@/components/TagConditionInput';
import { LANGUAGES } from '@/features/tour-operators/wizard/languages';

import {
  eventActions,
  otaActions,
  tagCategoryCodeActions,
  tourActions,
  tourTagActions,
} from '../../notificationLinks';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { OTA_TRISTATE_OPTIONS, RULE_DELIVERY_METHOD_OPTIONS } from '../ruleCatalog';
import { RulesetPreview } from '../RulesetPreview';
import type { NestedRulesetRef } from '../ruleTypes';
import { tourTagsFromArgs, tourTagsToArgs } from '../tourTagArgs';
import type { ArgEditorProps } from './common';
import { argsOf, BoolArg, stringList } from './common';

function tenantParam(tenantId: number | 'system'): number | 'system' {
  return tenantId;
}

// ---------------------------------------------------------------------------
// tpls 8/43 — {country: ["us", …]} (ISO code strings, as stored)
// ---------------------------------------------------------------------------

interface CountryOption {
  code: string;
  name: string;
}

export function CountryArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const raw = args.country;
  const selected: CountryOption[] = (Array.isArray(raw) ? raw : raw ? [raw] : []).map((c) => ({
    code: String(c),
    name: String(c).toUpperCase(),
  }));
  const queryFn = useCallback(async (q: string) => {
    const res = await listCountries(q);
    return res.results
      .filter((c) => c.country_code)
      .map((c) => ({ code: String(c.country_code), name: c.name }));
  }, []);
  return (
    <RemoteAutocomplete<CountryOption>
      queryFn={queryFn}
      getOptionLabel={(o) => o.name}
      getOptionKey={(o) => o.code.toLowerCase()}
      value={selected}
      multiple
      fetchOnEmpty
      size="small"
      disabled={disabled}
      label="Countries"
      placeholder={selected.length ? '' : 'Any non-empty selection required'}
      onChange={(v) =>
        onArgsChange({ ...args, country: ((v as CountryOption[] | null) ?? []).map((o) => o.code) })
      }
    />
  );
}

// ---------------------------------------------------------------------------
// tpl 9 — {lang: ["en", …] | null} (null when emptied — dashboard behavior)
// ---------------------------------------------------------------------------

interface LanguageOption {
  code: string;
  label: string;
}

export function LanguageArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const raw = args.lang;
  const byCode = new Map(LANGUAGES.map((l) => [l.code, l.label]));
  const selected: LanguageOption[] = (Array.isArray(raw) ? raw : raw ? [raw] : []).map((c) => ({
    code: String(c),
    label: byCode.get(String(c)) ?? String(c),
  }));
  return (
    <Autocomplete<LanguageOption, true>
      multiple
      size="small"
      disabled={disabled}
      options={LANGUAGES.map((l) => ({ code: l.code, label: l.label }))}
      getOptionLabel={(o) => `${o.label} (${o.code})`}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      value={selected}
      onChange={(_, v) => onArgsChange({ ...args, lang: v.length ? v.map((o) => o.code) : null })}
      renderInput={(params) => (
        <TextField {...params} label="Languages" placeholder={selected.length ? '' : 'Pick at least one'} />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// tpl 10 — {ota: [{id, name}], unique_emails, direct, app_promotion}
// (dashboard stores full OTA objects but renders only `.name`; the matcher
// reads only the id — {id, name} is the minimal dashboard-safe shape)
// ---------------------------------------------------------------------------

interface OTAOption {
  id: number;
  name: string;
}

export function OtaArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const selected = (Array.isArray(args.ota) ? args.ota : []) as OTAOption[];
  const queryFn = useCallback(async (q: string) => {
    const res = await otasApi.list({ search: q || undefined, pageSize: 50 });
    return res.results.map((o) => ({ id: o.id, name: o.name }));
  }, []);
  const triState = (key: 'unique_emails' | 'direct' | 'app_promotion', label: string) => (
    <TextField
      select
      size="small"
      label={label}
      sx={{ width: 220 }}
      value={Number(args[key] ?? 0)}
      disabled={disabled}
      onChange={(e) => onArgsChange({ ...argsOf(rule), [key]: Number(e.target.value) })}
    >
      {OTA_TRISTATE_OPTIONS[key].map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Stack spacing={1.5}>
      <RemoteAutocomplete<OTAOption>
        queryFn={queryFn}
        getOptionLabel={(o) => o.name}
        getOptionKey={(o) => o.id}
        value={selected}
        multiple
        fetchOnEmpty
        size="small"
        disabled={disabled}
        label="OTAs"
        placeholder={selected.length ? '' : 'Leave empty to match any OTA'}
        onChange={(v) =>
          onArgsChange({
            ...args,
            ota: ((v as OTAOption[] | null) ?? []).map((o) => ({ id: o.id, name: o.name })),
          })
        }
      />
      <PickerLinks
        items={selected.map((o) => ({ key: o.id, label: o.name, actions: otaActions(o) }))}
      />
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        {triState('unique_emails', 'Unique emails')}
        {triState('direct', 'Direct/internal')}
        {triState('app_promotion', 'App promotion')}
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// tpls 33/50/52/54 — {tag_categories: ["<code>", …]}
// ---------------------------------------------------------------------------

interface CategoryOption {
  code: string;
  name: string;
}

export function TagCategoryArgs({ rule, onArgsChange, tenantId, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const selected: CategoryOption[] = stringList(args.tag_categories).map((code) => ({
    code,
    name: code,
  }));
  const queryFn = useCallback(
    async (q: string) => {
      // System templates match against every tenant's categories — leave the
      // scope open there; tenant templates search their own + system rows.
      const res = await tagCategoriesApi.list({
        search: q || undefined,
        ...(tenantId !== 'system' ? { tour_operator_id: tenantId } : {}),
        pageSize: 50,
      });
      const seen = new Set<string>();
      return res.results
        .filter((c) => (seen.has(c.code) ? false : (seen.add(c.code), true)))
        .map((c) => ({ code: c.code, name: c.name }));
    },
    [tenantId],
  );
  const picker = (
    <RemoteAutocomplete<CategoryOption>
      queryFn={queryFn}
      getOptionLabel={(o) => (o.name && o.name !== o.code ? `${o.code} — ${o.name}` : o.code)}
      getOptionKey={(o) => o.code}
      value={selected}
      multiple
      fetchOnEmpty
      size="small"
      disabled={disabled}
      label="Tag categories"
      onChange={(v) =>
        onArgsChange({ ...args, tag_categories: ((v as CategoryOption[] | null) ?? []).map((o) => o.code) })
      }
    />
  );
  return (
    <Box>
      {picker}
      <PickerLinks
        items={selected.map((c) => ({
          key: c.code,
          label: c.code,
          // The rule stores the CODE, not the category id — no category page
          // to open, but the tags and bookings under it are reachable.
          actions: tagCategoryCodeActions(c.code, {
            tenantId: tenantId === 'system' ? null : tenantId,
          }),
        }))}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// tpls 34/51/53/55 — {tags: [{id, name, category: "<code>"}, …]}
// ---------------------------------------------------------------------------

interface TagOption {
  id: number;
  name: string;
  category: string;
}

export function TagArgs({ rule, onArgsChange, tenantId, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const selected = (Array.isArray(args.tags) ? args.tags : []) as TagOption[];
  const queryFn = useCallback(
    async (q: string) => {
      const res = await tagsApi.list({
        search: q || undefined,
        ...(tenantId !== 'system' ? { tour_operator_id: tenantId } : {}),
        pageSize: 50,
      });
      return res.results.map((t) => ({ id: t.id, name: t.name, category: t.category.code }));
    },
    [tenantId],
  );
  const picker = (
    <RemoteAutocomplete<TagOption>
      queryFn={queryFn}
      getOptionLabel={(o) => `${o.category}: ${o.name}`}
      getOptionKey={(o) => o.id}
      value={selected}
      multiple
      fetchOnEmpty
      size="small"
      disabled={disabled}
      label="Tags"
      onChange={(v) =>
        onArgsChange({
          ...args,
          tags: ((v as TagOption[] | null) ?? []).map((o) => ({
            id: o.id,
            name: o.name,
            category: o.category,
          })),
        })
      }
    />
  );
  return (
    <Box>
      {picker}
      <PickerLinks
        items={selected.map((t) => ({
          key: t.id,
          label: `${t.category}: ${t.name}`,
          actions: tagChipActions(
            { id: t.id, name: t.name, category: { code: t.category } },
            { tenantId: tenantId === 'system' ? null : tenantId, owner: 'booking' },
          ),
        }))}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// tpl 45 — {same_departure, same_tour, time: {future, active, past},
//           time_ref: {future|active|past: "now"|"start"|"end"},
//           tours: [...], tour_tags: {match_mode, tags: [...]}}
// (dashboard's tour labels read name/is_bespoke/start_date/end_date — store
//  them; time_ref/tour_tags are CP-8264, absent = legacy behavior)
// ---------------------------------------------------------------------------

interface TourOption {
  id: number;
  name: string;
  is_bespoke: boolean;
  start_date: string | null;
  end_date: string | null;
}

function tourLabel(t: TourOption): string {
  const base = `[id:${t.id}] ${t.name}`;
  if (!t.is_bespoke) return `${base} (Recurring)`;
  return `${base} (${t.start_date ?? '?'} - ${t.end_date ?? '?'})`;
}

const TIME_REF_OPTIONS = [
  { value: 'now', label: 'Fire time' },
  { value: 'start', label: 'Booking start' },
  { value: 'end', label: 'Booking end' },
] as const;

// The tag-condition widget never imports API modules; bind it to the ruleset
// resource's lookups (readable with any of the three editor permissions).
const TOUR_TAG_FETCHERS: TagConditionFetchers = {
  fetchTenantCategories: (tid, q, signal) =>
    notificationRulesetTagLookups.tenantCategories(tid, q, signal),
  fetchAllCategoryCodes: (q, signal) => notificationRulesetTagLookups.categoryCodes(q, signal),
  fetchCategoryTags: (categoryId, q, signal) =>
    notificationRulesetTagLookups.categoryTags(categoryId, q, signal),
  fetchTagsForCode: (code, q, signal) => notificationRulesetTagLookups.tagsForCode(code, q, signal),
  fetchCanonicalsForCode: (code, q, signal) =>
    notificationRulesetTagLookups.canonicalsForCode(code, q, signal),
};

export function OtherToursArgs({ rule, onArgsChange, tenantId, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const time = (args.time ?? {}) as Record<string, unknown>;
  const rawRefs = (args.time_ref && typeof args.time_ref === 'object' ? args.time_ref : {}) as Record<
    string,
    unknown
  >;
  const selected = (Array.isArray(args.tours) ? args.tours : []) as TourOption[];
  const tourTags = tourTagsFromArgs(args.tour_tags);
  const queryFn = useCallback(
    async (q: string) => {
      const res = await toursApi.list({
        search: q || undefined,
        ...(tenantId !== 'system' ? { tour_operator_id: tenantId } : {}),
        pageSize: 50,
      });
      return res.results.map((t) => ({
        id: t.id,
        name: t.name,
        is_bespoke: t.is_bespoke,
        start_date: t.start_date,
        end_date: t.end_date,
      }));
    },
    [tenantId],
  );
  const timeToggle = (key: 'future' | 'active' | 'past', label: string) => (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={Boolean(time[key])}
          disabled={disabled}
          onChange={(e) => onArgsChange({ ...args, time: { ...time, [key]: e.target.checked } })}
        />
      }
      label={label}
    />
  );
  const refValue = (key: 'future' | 'active' | 'past'): string =>
    typeof rawRefs[key] === 'string' ? (rawRefs[key] as string) : 'now';
  const timeRefSelect = (key: 'future' | 'active' | 'past') => (
    <TextField
      select
      size="small"
      label="Relative to"
      sx={{ width: 150 }}
      value={refValue(key)}
      disabled={disabled || !time[key]}
      onChange={(e) => onArgsChange({ ...args, time_ref: { ...rawRefs, [key]: e.target.value } })}
    >
      {TIME_REF_OPTIONS.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <BoolArg rule={rule} onArgsChange={onArgsChange} disabled={disabled} argKey="same_departure" label="Same tour, date & time" />
        <BoolArg rule={rule} onArgsChange={onArgsChange} disabled={disabled} argKey="same_tour" label="Same tour" />
      </Stack>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
        <Stack direction="row" spacing={0.5} alignItems="center">
          {timeToggle('future', 'Future tours')}
          {timeRefSelect('future')}
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {timeToggle('active', 'Active tours')}
          {timeRefSelect('active')}
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {timeToggle('past', 'Past tours')}
          {timeRefSelect('past')}
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        “Booking start/end” evaluate against the triggering booking (or the order’s first booking
        start / last booking end). A flag whose reference can’t be resolved matches nothing.
      </Typography>
      <RemoteAutocomplete<TourOption>
        queryFn={queryFn}
        getOptionLabel={tourLabel}
        getOptionKey={(o) => o.id}
        value={selected}
        multiple
        fetchOnEmpty
        size="small"
        disabled={disabled}
        label="Tours"
        placeholder={selected.length ? '' : 'Leave empty to match any tour — search by name or id:1234'}
        onChange={(v) =>
          onArgsChange({
            ...args,
            tours: ((v as TourOption[] | null) ?? []).map((t) => ({
              id: t.id,
              name: t.name,
              is_bespoke: t.is_bespoke,
              start_date: t.start_date,
              end_date: t.end_date,
            })),
          })
        }
      />
      <PickerLinks
        items={selected.map((t) => ({
          key: t.id,
          label: t.name,
          actions: tourActions(t, { tenantId: tenantId === 'system' ? null : tenantId }),
        }))}
      />
      <Box>
        <TagConditionInput
          value={tourTags}
          onChange={(v) => onArgsChange({ ...args, tour_tags: tourTagsToArgs(v) })}
          fetchers={TOUR_TAG_FETCHERS}
          label="Tours tagged with"
          disabled={disabled}
          fixedTenant={tenantId !== 'system' ? { id: tenantId } : undefined}
          helperText="Also match tours carrying these tags — combined with the tour list above (either side matches)."
        />
        {/* Only PINNED chips name one row; a category_code/tag_name chip is a
            late-bound match with no single tag to open. */}
        <PickerLinks
          prefix="Pinned tags:"
          items={tourTags.chips.flatMap((chip) =>
            'tag_id' in chip.spec
              ? [
                  {
                    key: chip.spec.tag_id,
                    label: `${chip.labels.category}: ${chip.labels.tag}`,
                    actions: tourTagActions(
                      { id: chip.spec.tag_id, name: chip.labels.tag },
                      { tenantId: tenantId === 'system' ? null : tenantId },
                    ),
                  },
                ]
              : [],
          )}
        />
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// tpl 49 — {events: [{value, label}, …]} (whole objects, dashboard shape)
// ---------------------------------------------------------------------------

interface EventOption {
  value: string;
  label: string;
}

export function EventTypeArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const selected = (Array.isArray(args.events) ? args.events : []) as EventOption[];
  const queryFn = useCallback(async (q: string) => {
    const res = await listEvents();
    const needle = q.toLowerCase();
    return res.events
      .filter(
        (e) =>
          !needle ||
          e.code.toLowerCase().includes(needle) ||
          (e.label ?? '').toLowerCase().includes(needle),
      )
      .map((e) => ({ value: e.code, label: e.label ?? e.code }));
  }, []);
  const picker = (
    <RemoteAutocomplete<EventOption>
      queryFn={queryFn}
      getOptionLabel={(o) => (o.label && o.label !== o.value ? `${o.label} (${o.value})` : o.value)}
      getOptionKey={(o) => o.value}
      value={selected}
      multiple
      fetchOnEmpty
      size="small"
      disabled={disabled}
      label="Event types"
      onChange={(v) =>
        onArgsChange({
          ...args,
          events: ((v as EventOption[] | null) ?? []).map((o) => ({ value: o.value, label: o.label })),
        })
      }
    />
  );
  return (
    <Box>
      {picker}
      <PickerLinks
        items={selected.map((e) => ({
          key: e.value,
          label: e.value,
          actions: eventActions(e.value),
        }))}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// tpl 56 — {delivery_methods: [{id: "<string>", name}], allow_undefined}
// ---------------------------------------------------------------------------

interface DeliveryMethodOption {
  id: string;
  name: string;
}

export function DeliveryMethodArgs(props: ArgEditorProps) {
  const { rule, onArgsChange, disabled } = props;
  const args = argsOf(rule);
  const selected = (Array.isArray(args.delivery_methods) ? args.delivery_methods : []) as DeliveryMethodOption[];
  return (
    <Stack spacing={0.5}>
      <Autocomplete<DeliveryMethodOption, true>
        multiple
        size="small"
        disabled={disabled}
        options={RULE_DELIVERY_METHOD_OPTIONS}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
        value={selected}
        onChange={(_, v) =>
          onArgsChange({
            ...args,
            delivery_methods: v.map((o) => ({ id: String(o.id), name: o.name })),
            // The backend matcher indexes args["allow_undefined"] unguarded —
            // always persist it alongside delivery_methods.
            allow_undefined: Boolean(args.allow_undefined),
          })
        }
        renderInput={(params) => <TextField {...params} label="Delivery methods" />}
      />
      <BoolArg {...props} argKey="allow_undefined" label="Ignore this rule when the delivery method is unknown" />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// tpl 48 — nested_ruleset (a RULE column, not args)
// ---------------------------------------------------------------------------

export function NestedRulesetArgs({
  rule,
  onRuleChange,
  tenantId,
  excludeRulesetId,
  disabled,
}: ArgEditorProps) {
  const selected = rule.nested_ruleset;
  const queryFn = useCallback(
    async (q: string) => {
      const res = await listRulesets({
        search: q || undefined,
        tour_operator_id: tenantParam(tenantId),
        pageSize: 25,
      });
      return res.results.filter((r) => r.id !== excludeRulesetId);
    },
    [tenantId, excludeRulesetId],
  );
  const asOption: RulesetRef | null = selected
    ? { id: selected.id, name: selected.name ?? `#${selected.id}`, codename: selected.codename ?? null, tenant: null }
    : null;
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
      <Stack spacing={0.5} sx={{ width: 380, maxWidth: '100%', flexShrink: 0 }}>
        <RemoteAutocomplete<RulesetRef>
          queryFn={queryFn}
          getOptionLabel={(o) => `${o.name} (#${o.id})`}
          getOptionKey={(o) => o.id}
          value={asOption}
          fetchOnEmpty
          size="small"
          disabled={disabled}
          label="Ruleset"
          onChange={(v) => {
            const opt = v as RulesetRef | null;
            onRuleChange({
              nested_ruleset: opt
                ? ({ id: opt.id, name: opt.name, codename: opt.codename } as NestedRulesetRef)
                : null,
            });
          }}
        />
        {selected && (
          <Typography variant="caption" color="text.secondary">
            <Link
              component={RouterLink}
              to={`/notifications/rulesets/${selected.id}`}
              target="_blank"
              rel="noopener"
            >
              Open ruleset #{selected.id}
            </Link>
          </Typography>
        )}
      </Stack>
      {selected && (
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <RulesetPreview rulesetId={selected.id} />
        </Box>
      )}
    </Stack>
  );
}
